// BUILD-042 cycle 13 (DOCS) - what the Documents step remembers while HR steps away (Manage forms, a document's "View ->") and comes back.
//
// Pure rules only: no DOM, no React, no network. The browser's sessionStorage is INJECTED (a tiny get/set/remove interface), so every rule is tested under plain Node
// (documentsRetention.test.mjs).
//
// What is kept: the ids of the forms HR ticked, the search text, and whether the form list was open. Nothing else: no names, no HR-entered values, nothing about the
// employee beyond their id. It is a convenience, never a record, never sent to the server, and it never assigns anything. Every restore is re-checked against the
// freshly loaded form list and the caller's CURRENT permission (revalidateRetained), so a form that was retired or assigned meanwhile is dropped, and a caller who can
// no longer assign sees nothing restored.
//
// Scoping: the storage key includes the signed-in caller's employee id AND the employee being set up, and the payload repeats both and is checked on read; a payload
// for anyone else is never returned (and a different caller's leftovers are swept). Payloads are versioned, time-stamped and expire after RETENTION_TTL_MS.

export const RETENTION_VERSION = 1;
export const RETENTION_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours
const KEY_PREFIX = 'b042.documents-step.v1';
const MAX_IDS = 500;
const MAX_ID_LENGTH = 200;
export const MAX_SEARCH_LENGTH = 200;
const FUTURE_SKEW_MS = 5 * 60 * 1000; // a stored time-stamp more than this far ahead of now is not trusted

/** The part of the Web Storage interface used here (sessionStorage satisfies it; a test double is a few lines). `length`/`key` are optional: without them nothing is swept. */
export interface RetentionStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  readonly length?: number;
  key?(index: number): string | null;
}

export interface RetentionScope { callerId: string | null | undefined; employeeId: string | null | undefined }
export interface RetainedState { selected: string[]; search: string; panelOpen: boolean }

const nonBlank = (v: unknown): v is string => typeof v === 'string' && v.trim().length > 0;

/** The storage key for this caller + employee, or null when either is unknown (nothing is then stored or read). */
export function retentionKey(scope: RetentionScope): string | null {
  if (!nonBlank(scope.callerId) || !nonBlank(scope.employeeId)) return null;
  return `${KEY_PREFIX}:${encodeURIComponent(scope.callerId)}:${encodeURIComponent(scope.employeeId)}`;
}

/** The post-setup Documents section's own scope: a different surface from the guided step, so the two never read each other's entries (same caller + same employee only). */
export function postSetupScope(callerId: string | null | undefined, employeeId: string | null | undefined): RetentionScope {
  return { callerId, employeeId: nonBlank(employeeId) ? `post-setup:${employeeId}` : employeeId };
}

/** Empty = nothing worth keeping: no ticked form and no search text. (An open/closed list on its own is not kept.) */
export function isEmptyRetained(s: RetainedState): boolean {
  return s.selected.length === 0 && s.search.trim() === '';
}

/** The stored text for this state, or null when the state is empty / the scope is unknown. */
export function encodeRetained(scope: RetentionScope, state: RetainedState, now: number): string | null {
  if (!retentionKey(scope) || isEmptyRetained(state)) return null;
  return JSON.stringify({
    v: RETENTION_VERSION, savedAt: now, callerId: scope.callerId, employeeId: scope.employeeId,
    selected: [...new Set(state.selected)].slice(0, MAX_IDS), search: state.search.slice(0, MAX_SEARCH_LENGTH), panelOpen: state.panelOpen === true,
  });
}

/** Parses stored text. Anything that is not exactly a current, unexpired payload for THIS caller + employee is null (never a partial or guessed value). */
export function decodeRetained(raw: string | null | undefined, scope: RetentionScope, now: number): RetainedState | null {
  if (!retentionKey(scope) || typeof raw !== 'string' || raw === '') return null;
  let p: unknown;
  try { p = JSON.parse(raw); } catch { return null; }
  if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
  const o = p as Record<string, unknown>;
  if (o.v !== RETENTION_VERSION) return null;
  if (typeof o.savedAt !== 'number' || !Number.isFinite(o.savedAt)) return null;
  if (now - o.savedAt > RETENTION_TTL_MS || o.savedAt - now > FUTURE_SKEW_MS) return null;
  if (o.callerId !== scope.callerId || o.employeeId !== scope.employeeId) return null;
  if (!Array.isArray(o.selected) || o.selected.length > MAX_IDS) return null;
  if (!o.selected.every((id) => typeof id === 'string' && id.length > 0 && id.length <= MAX_ID_LENGTH)) return null;
  if (typeof o.search !== 'string' || o.search.length > MAX_SEARCH_LENGTH) return null;
  if (typeof o.panelOpen !== 'boolean') return null;
  const state: RetainedState = { selected: [...new Set(o.selected as string[])], search: o.search, panelOpen: o.panelOpen };
  return isEmptyRetained(state) ? null : state;
}

/** Reads this scope's retained state. A payload that is expired, corrupt, old-version or for someone else is removed (best effort) and reads as null. */
export function loadRetained(storage: RetentionStorage | null | undefined, scope: RetentionScope, now: number): RetainedState | null {
  const key = retentionKey(scope);
  if (!storage || !key) return null;
  try {
    const raw = storage.getItem(key);
    if (raw === null) return null;
    const state = decodeRetained(raw, scope, now);
    if (!state) storage.removeItem(key);
    return state;
  } catch { return null; }
}

/** Stores the state, or removes the entry when it is empty. A storage failure (private mode, quota) is swallowed: retention is a convenience. */
export function saveRetained(storage: RetentionStorage | null | undefined, scope: RetentionScope, state: RetainedState, now: number): void {
  const key = retentionKey(scope);
  if (!storage || !key) return;
  try {
    const text = encodeRetained(scope, state, now);
    if (text === null) storage.removeItem(key); else storage.setItem(key, text);
  } catch { /* nothing to do */ }
}

export function clearRetained(storage: RetentionStorage | null | undefined, scope: RetentionScope): void {
  const key = retentionKey(scope);
  if (!storage || !key) return;
  try { storage.removeItem(key); } catch { /* nothing to do */ }
}

/** Removes this step's entries that belong to a DIFFERENT caller (a different person signed in on this tab) or are unreadable/expired. Entries of this caller for other employees stay until they expire. */
export function sweepRetained(storage: RetentionStorage | null | undefined, callerId: string | null | undefined, now: number): number {
  if (!storage || !nonBlank(callerId) || typeof storage.length !== 'number' || typeof storage.key !== 'function') return 0;
  const mine = `${KEY_PREFIX}:${encodeURIComponent(callerId)}:`;
  const doomed: string[] = [];
  try {
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (!k || !k.startsWith(`${KEY_PREFIX}:`)) continue;
      if (!k.startsWith(mine)) { doomed.push(k); continue; }
      const employeeId = decodeURIComponent(k.slice(mine.length));
      if (!decodeRetained(storage.getItem(k), { callerId, employeeId }, now)) doomed.push(k);
    }
    for (const k of doomed) storage.removeItem(k);
  } catch { /* nothing to do */ }
  return doomed.length;
}

export interface RestoreResult {
  selected: Set<string>;
  search: string;
  panelOpen: boolean;
  /** How many remembered forms could not be restored (retired, no longer listed, or already assigned). */
  dropped: number;
}

/**
 * Re-checks remembered state against what is true NOW. `assignableIds` = the ids of the forms that are currently selectable for this person (from the freshly loaded
 * form list and the freshly read assignments). A remembered id that is not in it is dropped. When the caller may not assign (`mayAssign` false), nothing at all is
 * restored. This never adds a form HR did not choose.
 */
export function revalidateRetained(state: RetainedState | null, assignableIds: Iterable<string>, mayAssign: boolean): RestoreResult {
  if (!state || !mayAssign) return { selected: new Set(), search: '', panelOpen: false, dropped: 0 };
  const ok = new Set(assignableIds);
  const selected = new Set(state.selected.filter((id) => ok.has(id)));
  return { selected, search: state.search, panelOpen: state.panelOpen, dropped: state.selected.length - selected.size };
}

/** The short neutral note shown when remembered forms were dropped. */
export function droppedNote(dropped: number): string | null {
  if (dropped <= 0) return null;
  return dropped === 1
    ? 'A form you selected is no longer available, so it was removed from your selection.'
    : `${dropped} forms you selected are no longer available, so they were removed from your selection.`;
}
